import { autoUpdate, flip, offset, size } from '@floating-ui/dom';
import { useFloating } from '@floating-ui/react';
import { forwardRef, useContext } from 'react';
import { mergeRefs } from 'react-merge-refs';
import { Transition, type TransitionStatus } from 'react-transition-group';

import Portal from '#@/plumbing/Portal';
import { CARD_OPENING_TRANSITION_DURATION } from '#@/shared/constants';
import QueryMatchesContext from '#@/shared/contexts/queryMatchesContext';
import { isEnter, isEntered, isExiting } from '#@/shared/utils/transitionStates';

import ManagedLayer, { type ManagedLayerProps } from '../ManagedLayer';
import { DROPDOWN_CARD_OVERLAP_WITH_TRIGGER } from './constants';
import MobileBackground from './MobileBackground';
import Card, { type CardProps, DROPDOWN_CARD_EDGE_OFFSET } from './Styles';

export interface DropdownCardProps extends Pick<ManagedLayerProps, 'onClose' | 'additionalRefs'> {
  isOpen: boolean;
  triggerRef: HTMLElement | null;
  matchTriggerWidth?: boolean;
  unmountOnExit?: boolean;
  /**
   * This is a prop that is used in <Transition>; we need to set both `in` and `appear` to true
   * in order to add the enter transition when it mounts
   * @see https://reactcommunity.org/react-transition-group/transition
   */
  appear?: boolean;
  fixedWidth?: number;
  maxWidth?: number;
  placement?: 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';
  children: React.ReactNode;
}

const DropdownCard = forwardRef<HTMLDivElement, DropdownCardProps>(
  (
    {
      children,
      isOpen,
      triggerRef,
      matchTriggerWidth = false,
      unmountOnExit = false,
      appear = false,
      fixedWidth,
      maxWidth,
      placement = 'bottom-end',
      onClose,
      additionalRefs = [],
      ...props
    },
    ref,
  ) => {
    const { isMobile } = useContext(QueryMatchesContext);

    const { refs, floatingStyles } = useFloating({
      open: isOpen,
      elements: { reference: triggerRef },
      whileElementsMounted: autoUpdate,
      placement,
      strategy: 'fixed',
      middleware: [
        ...[
          matchTriggerWidth &&
            !fixedWidth &&
            size({
              apply({ rects, elements }) {
                requestAnimationFrame(() => {
                  Object.assign(elements.floating.style, {
                    width: `${rects.reference.width}px`,
                  });
                });
              },
            }),
        ],
        offset(-DROPDOWN_CARD_OVERLAP_WITH_TRIGGER),
        flip({ padding: DROPDOWN_CARD_EDGE_OFFSET }),
        size({
          apply({ availableWidth, availableHeight, elements }) {
            requestAnimationFrame(() => {
              Object.assign(elements.floating.style, {
                maxWidth: `${availableWidth}px`,
                maxHeight: `${availableHeight}px`,
              });
            });
          },
          padding: DROPDOWN_CARD_EDGE_OFFSET,
        }),
      ],
    });

    const renderCard = (state: TransitionStatus, cardProps: Partial<CardProps>) => (
      <Card
        data-cy-dropdown-card-open={isEntered(state)}
        ref={mergeRefs([refs.setFloating, ref])}
        $isOpen={isEnter(state)}
        style={isMobile ? {} : floatingStyles}
        {...cardProps}
      >
        {children}
      </Card>
    );

    const renderWrapper = (state: TransitionStatus) => {
      if (isMobile) {
        return <MobileBackground isOpen={isOpen}>{renderCard(state, props)}</MobileBackground>;
      }

      return renderCard(state, { $fixedWidth: fixedWidth, $maxWidth: maxWidth, ...props });
    };

    return (
      <Transition
        in={isOpen}
        timeout={CARD_OPENING_TRANSITION_DURATION}
        unmountOnExit={unmountOnExit}
        appear={appear}
      >
        {(state) => (
          <ManagedLayer
            isOpen={isEnter(state) || isExiting(state)}
            onClose={onClose}
            wrapperRef={{ current: refs.floating.current }}
            triggerRef={{ current: triggerRef }}
            additionalRefs={additionalRefs}
          >
            <Portal>{renderWrapper(state)}</Portal>
          </ManagedLayer>
        )}
      </Transition>
    );
  },
);

DropdownCard.displayName = 'DropdownCard';

export default DropdownCard;
